/**
 * Advanced Color Detection Service
 * Extracts dominant fish colors from images, ignoring backgrounds
 */

// Color name mappings
const COLOR_MAP = {
  'red': ['أحمر', 'red', 'قرمز', 'crimson', 'scarlet'],
  'pink': ['وردي', 'pink', 'rose'],
  'orange': ['برتقالي', 'orange'],
  'yellow': ['أصفر', 'yellow', 'gold', 'ذهبي'],
  'gold': ['ذهبي', 'gold', 'أصفر', 'yellow'],
  'green': ['أخضر', 'green', 'verde'],
  'turquoise': ['تركواز', 'turquoise', 'cyan', 'سيان'],
  'cyan': ['سيان', 'cyan', 'turquoise', 'تركواز'],
  'blue': ['أزرق', 'blue', 'azure'],
  'navy': ['كحلي', 'navy', 'dark blue'],
  'purple': ['بنفسجي', 'purple', 'violet', 'viola'],
  'violet': ['بنفسجي', 'violet', 'purple'],
  'white': ['أبيض', 'white', 'snow'],
  'gray': ['رمادي', 'gray', 'grey', 'silver', 'فضي'],
  'black': ['أسود', 'black', 'dark'],
  'brown': ['بني', 'brown', 'tan'],
};

/**
 * Converts RGB to HSL
 */
function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/**
 * Maps RGB color to a color name
 */
function rgbToColorName(r, g, b) {
  const [h, s, l] = rgbToHsl(r, g, b);

  // Low saturation = gray/white/black
  if (s < 25) {
    if (l > 85) return 'white';
    if (l < 20) return 'black';
    return 'gray';
  }

  // Map hue to color names with better ranges
  if (h >= 0 && h < 15) return 'red';
  if (h >= 15 && h < 35) return 'orange';
  if (h >= 35 && h < 65) return 'yellow';
  if (h >= 65 && h < 160) return 'green';
  if (h >= 160 && h < 190) return 'cyan';
  if (h >= 190 && h < 240) return 'blue';
  if (h >= 240 && h < 260) return 'navy';
  if (h >= 260 && h < 300) return 'purple';
  if (h >= 300 && h < 330) return 'pink';
  return 'red';
}

/**
 * Gets the cache key for an image URL
 */
function getCacheKey(imageUrl) {
  // Use a simple hash of the URL
  let hash = 0;
  for (let i = 0; i < imageUrl.length; i++) {
    const char = imageUrl.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `color_${Math.abs(hash)}`;
}

/**
 * Caches colors in localStorage
 */
function cacheColors(imageUrl, colors) {
  try {
    const cacheKey = getCacheKey(imageUrl);
    const cache = JSON.parse(localStorage.getItem('colorCache') || '{}');
    cache[cacheKey] = {
      colors,
      timestamp: Date.now()
    };
    // Keep cache under 5MB (roughly 5000 entries)
    const entries = Object.keys(cache);
    if (entries.length > 5000) {
      // Remove oldest 20% of entries
      const sorted = entries
        .map(key => ({ key, timestamp: cache[key].timestamp }))
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(0, Math.floor(entries.length * 0.2))
        .map(item => item.key);
      sorted.forEach(key => delete cache[key]);
    }
    localStorage.setItem('colorCache', JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to cache colors:', e);
  }
}

/**
 * Gets cached colors for an image URL
 */
function getCachedColors(imageUrl) {
  try {
    const cacheKey = getCacheKey(imageUrl);
    const cache = JSON.parse(localStorage.getItem('colorCache') || '{}');
    const cached = cache[cacheKey];
    if (cached && cached.colors && cached.colors.length > 0) {
      return cached.colors;
    }
  } catch (e) {
    console.warn('Failed to get cached colors:', e);
  }
  return null;
}

/**
 * Analyzes edge colors to determine background
 */
function analyzeBackground(imageData, width, height) {
  const edgePixels = [];
  const edgeSize = Math.min(width, height) * 0.15; // 15% of smallest dimension
  
  // Sample edges
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const isEdge = x < edgeSize || x >= width - edgeSize || 
                     y < edgeSize || y >= height - edgeSize;
      if (isEdge) {
        const idx = (y * width + x) * 4;
        const r = imageData.data[idx];
        const g = imageData.data[idx + 1];
        const b = imageData.data[idx + 2];
        const a = imageData.data[idx + 3];
        if (a > 200) {
          edgePixels.push([r, g, b]);
        }
      }
    }
  }
  
  // Find dominant edge colors (likely background)
  if (edgePixels.length === 0) return new Set();
  
  const edgeColorCounts = new Map();
  edgePixels.forEach(([r, g, b]) => {
    const colorName = rgbToColorName(r, g, b);
    edgeColorCounts.set(colorName, (edgeColorCounts.get(colorName) || 0) + 1);
  });
  
  // Return colors that appear in >30% of edge pixels
  const threshold = edgePixels.length * 0.3;
  const backgroundColors = new Set();
  edgeColorCounts.forEach((count, color) => {
    if (count > threshold) {
      backgroundColors.add(color);
    }
  });
  
  return backgroundColors;
}

/**
 * Extracts dominant colors from an image, focusing on fish
 */
export async function extractColorsFromImage(imageUrl, sampleSize = 100) {
  // Check cache first
  const cached = getCachedColors(imageUrl);
  if (cached) {
    return cached;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Resize for performance
        const maxDimension = 400;
        const scale = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const width = canvas.width;
        const height = canvas.height;
        
        // Analyze background from edges
        const backgroundColors = analyzeBackground(imageData, width, height);
        
        // Sample center area with grid-based approach
        const centerX = width / 2;
        const centerY = height / 2;
        const centerWidth = width * 0.5; // 50% of width
        const centerHeight = height * 0.6; // 60% of height
        const startX = centerX - centerWidth / 2;
        const startY = centerY - centerHeight / 2;
        const endX = centerX + centerWidth / 2;
        const endY = centerY + centerHeight / 2;
        
        // Use grid-based sampling for consistency
        const gridSize = Math.ceil(Math.sqrt(sampleSize));
        const stepX = centerWidth / gridSize;
        const stepY = centerHeight / gridSize;
        
        const colorMap = new Map();
        const excludedColors = ['white', 'black', 'gray', ...Array.from(backgroundColors)];
        
        // Sample center area in a grid pattern
        for (let gy = 0; gy < gridSize; gy++) {
          for (let gx = 0; gx < gridSize; gx++) {
            const x = Math.floor(startX + gx * stepX + stepX * 0.5);
            const y = Math.floor(startY + gy * stepY + stepY * 0.5);
            
            if (x >= 0 && x < width && y >= 0 && y < height) {
              const idx = (y * width + x) * 4;
              const r = imageData.data[idx];
              const g = imageData.data[idx + 1];
              const b = imageData.data[idx + 2];
              const a = imageData.data[idx + 3];
              
              if (a < 200) continue;
              
              const [h, s, l] = rgbToHsl(r, g, b);
              
              // Skip background-like colors
              if (s < 30) continue; // Low saturation
              if (l < 15 || l > 90) continue; // Too dark or too light
              
              const colorName = rgbToColorName(r, g, b);
              
              // Skip excluded colors (backgrounds, etc.)
              if (excludedColors.includes(colorName)) continue;
              
              // Prefer saturated colors (fish are usually colorful)
              const weight = Math.min(s / 100, 1) * (1 - Math.abs(l - 50) / 50);
              colorMap.set(colorName, (colorMap.get(colorName) || 0) + weight);
            }
          }
        }
        
        // Also sample some random points for diversity
        const randomSamples = Math.floor(sampleSize * 0.2);
        for (let i = 0; i < randomSamples; i++) {
          const x = Math.floor(startX + Math.random() * centerWidth);
          const y = Math.floor(startY + Math.random() * centerHeight);
          
          if (x >= 0 && x < width && y >= 0 && y < height) {
            const idx = (y * width + x) * 4;
            const r = imageData.data[idx];
            const g = imageData.data[idx + 1];
            const b = imageData.data[idx + 2];
            const a = imageData.data[idx + 3];
            
            if (a < 200) continue;
            
            const [h, s, l] = rgbToHsl(r, g, b);
            
            // Only accept vibrant colors for random samples
            if (s < 40 || l < 20 || l > 85) continue;
            
            const colorName = rgbToColorName(r, g, b);
            if (excludedColors.includes(colorName)) continue;
            
            const weight = Math.min(s / 100, 1) * (1 - Math.abs(l - 50) / 50);
            colorMap.set(colorName, (colorMap.get(colorName) || 0) + weight * 0.5);
          }
        }
        
        // Get top 3 colors, excluding backgrounds
        const dominantColors = Array.from(colorMap.entries())
          .filter(([color]) => !excludedColors.includes(color))
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([color]) => color);
        
        // Cache the result
        const result = dominantColors.length > 0 ? dominantColors : ['unknown'];
        cacheColors(imageUrl, result);
        
        resolve(result);
      } catch (error) {
        console.error('Error extracting colors:', error);
        resolve(['unknown']);
      }
    };
    
    img.onerror = () => {
      console.error('Error loading image for color extraction:', imageUrl);
      resolve(['unknown']);
    };
    
    img.src = imageUrl;
  });
}

/**
 * Extracts colors for a guppy (from male and/or female image)
 */
export async function extractGuppyColors(guppy) {
  const colors = new Set();
  
  try {
    // Extract from male image
    if (guppy.maleimage) {
      const maleColors = await extractColorsFromImage(guppy.maleimage);
      maleColors.forEach(c => {
        if (c !== 'unknown') colors.add(c);
      });
    }
    
    // Extract from female image
    if (guppy.femaleimage) {
      const femaleColors = await extractColorsFromImage(guppy.femaleimage);
      femaleColors.forEach(c => {
        if (c !== 'unknown') colors.add(c);
      });
    }
  } catch (error) {
    console.error('Error extracting guppy colors:', error);
  }
  
  const result = Array.from(colors);
  return result.length > 0 ? result : [];
}

/**
 * Gets all available colors from a list of guppies
 */
export function getAvailableColors(guppies) {
  const colorSet = new Set();
  
  guppies.forEach(guppy => {
    if (guppy.colors && Array.isArray(guppy.colors)) {
      guppy.colors.forEach(color => {
        if (color && color !== 'unknown') {
          colorSet.add(color);
        }
      });
    }
  });
  
  return Array.from(colorSet).sort();
}

/**
 * Checks if a guppy matches a selected color
 */
export function matchesColor(guppy, selectedColor) {
  if (!selectedColor || selectedColor === 'all') return true;
  if (!guppy.colors || !Array.isArray(guppy.colors) || guppy.colors.length === 0) return false;
  
  // Direct match
  if (guppy.colors.includes(selectedColor)) return true;
  
  // Check color map aliases
  if (COLOR_MAP[selectedColor]) {
    return guppy.colors.some(color => 
      COLOR_MAP[selectedColor].includes(color) || 
      COLOR_MAP[color]?.includes(selectedColor)
    );
  }
  
  return false;
}

/**
 * Checks if a search query matches a color name (in any language)
 */
export function searchMatchesColor(searchQuery) {
  if (!searchQuery || searchQuery.trim().length === 0) return null;
  
  const query = searchQuery.toLowerCase().trim();
  
  // Check if query matches any color name or translation
  for (const [colorName, translations] of Object.entries(COLOR_MAP)) {
    // Check if query matches the color name itself
    if (colorName.toLowerCase() === query) return colorName;
    
    // Check if query matches any translation
    if (translations.some(translation => translation.toLowerCase() === query)) {
      return colorName;
    }
    
    // Check if query contains or is contained in translation
    if (translations.some(translation => 
      translation.toLowerCase().includes(query) || 
      query.includes(translation.toLowerCase())
    )) {
      return colorName;
    }
  }
  
  // Also check color translations directly
  for (const [colorName, translation] of Object.entries(COLOR_TRANSLATIONS)) {
    if (colorName === 'all' || colorName === 'unknown') continue;
    
    if (translation.toLowerCase() === query || 
        translation.toLowerCase().includes(query) ||
        query.includes(translation.toLowerCase())) {
      return colorName;
    }
  }
  
  return null;
}

/**
 * Color name translations for UI
 */
export const COLOR_TRANSLATIONS = {
  'all': 'الكل',
  'red': 'أحمر',
  'pink': 'وردي',
  'orange': 'برتقالي',
  'yellow': 'أصفر',
  'gold': 'ذهبي',
  'green': 'أخضر',
  'turquoise': 'تركواز',
  'cyan': 'سيان',
  'blue': 'أزرق',
  'navy': 'كحلي',
  'purple': 'بنفسجي',
  'violet': 'بنفسجي',
  'white': 'أبيض',
  'gray': 'رمادي',
  'black': 'أسود',
  'brown': 'بني',
  'unknown': 'غير معروف',
};

/**
 * Color display colors (for UI)
 */
export const COLOR_HEX_MAP = {
  'red': '#ef4444',
  'pink': '#ec4899',
  'orange': '#f97316',
  'yellow': '#eab308',
  'gold': '#fbbf24',
  'green': '#22c55e',
  'turquoise': '#14b8a6',
  'cyan': '#06b6d4',
  'blue': '#3b82f6',
  'navy': '#1e3a8a',
  'purple': '#a855f7',
  'violet': '#9333ea',
  'white': '#f8fafc',
  'gray': '#64748b',
  'black': '#0f172a',
  'brown': '#92400e',
  'unknown': '#64748b',
};
